import { useEffect, useRef, useState } from 'react';
import { ClockIcon, WarningIcon } from '@phosphor-icons/react';
import axios from 'axios';
import { useDispatch } from 'react-redux';
import toast from 'react-hot-toast';
import { setUser as updateUser } from '../../../../../apps/frontend/src/redux/slices/userSlice';
import { useMediaQuery } from '../../utils/screenWidth';
import ModalCloseButton from '../../components/generic/ModalCloseButton';

// Same toggle used on the Profile > Settings screen.
function ToggleSwitch({ id, checked, disabled, onChange }) {
  return (
    <label
      className={`relative inline-flex items-center ${disabled ? 'cursor-not-allowed opacity-60' : 'cursor-pointer'}`}
    >
      <input
        type="checkbox"
        id={id}
        checked={checked}
        disabled={disabled}
        onChange={(e) => onChange(e.target.checked)}
        className="sr-only"
      />
      <div
        className={`relative w-11 h-6 rounded-full transition-colors duration-200 ease-in-out ${
          checked ? 'bg-[var(--q3-accent-normal)]' : 'bg-[var(--q3-stroke-normal)]'
        }`}
      >
        <div
          className={`absolute top-0.5 left-0.5 w-5 h-5 bg-[var(--q3-surface-default)] rounded-full transition-transform duration-200 ease-in-out ${
            checked ? 'translate-x-5' : 'translate-x-0'
          }`}
        />
      </div>
    </label>
  );
}

function ModalBody({ onStart }) {
  const dispatch = useDispatch();
  const [timerEnabled, setTimerEnabled] = useState(false);
  const [syncing, setSyncing] = useState(false);

  useEffect(() => {
    try {
      const cachedUser = JSON.parse(localStorage.getItem('userProfile'));
      setTimerEnabled(!!cachedUser?.settings?.showQuestionLevelTimer);
    } catch (error) {
      /* ignore */
    }
  }, []);

  const handleToggle = async (newValue) => {
    const previous = timerEnabled;
    setTimerEnabled(newValue);
    setSyncing(true);
    try {
      const cachedUser = JSON.parse(localStorage.getItem('userProfile')) || {};
      const updatedUser = {
        ...cachedUser,
        settings: { ...cachedUser.settings, showQuestionLevelTimer: newValue },
      };
      localStorage.setItem('userProfile', JSON.stringify(updatedUser));
      dispatch(updateUser(updatedUser));

      const response = await axios.patch('/api/user/profile/settings', {
        showQuestionLevelTimer: newValue,
      });

      if (response.status === 200 && response.data?.data) {
        const apiUser = response.data.data;
        localStorage.setItem('userProfile', JSON.stringify(apiUser));
        dispatch(updateUser(apiUser));
        setTimerEnabled(!!apiUser.settings?.showQuestionLevelTimer);
      }
    } catch (error) {
      setTimerEnabled(previous);
      toast.error(error.response?.data?.message || 'Failed to update setting');
    } finally {
      setSyncing(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 items-center">
      {/* Heading */}
      <div className="flex flex-col gap-2 items-center justify-center w-full">
        <span className="relative overflow-hidden rounded-full bg-[var(--q3-danger-normal)] px-2.5 py-1 text-xs font-medium leading-4 text-[var(--q3-danger-light)] after:pointer-events-none after:absolute after:inset-y-0 after:left-0 after:w-1/3 after:bg-gradient-to-r after:from-transparent after:via-white/70 after:to-transparent after:animate-badge-shimmer">
          New Feature
        </span>
        <p className="text-xl font-bold leading-[26px] text-center text-[var(--q3-neutral-default)]">
          Track time per question
        </p>
        <p className="w-full text-sm leading-5 text-center text-[var(--q3-neutral-default)]">
          Get a nudge when you&apos;re spending too long
        </p>
      </div>

      {/* Preview + setting */}
      <div className="flex flex-col gap-6 items-start pt-3 w-full">
        {/* Quiz timer preview */}
        <div className="w-full rounded-xl bg-[var(--q3-surface-dim)] p-3">
          <div className="w-full flex flex-col gap-2 rounded-[10px] border border-[var(--q3-stroke-normal)] bg-[var(--q3-surface-default)] px-4 py-3.5">
            <div className="flex items-center justify-between w-full">
              <p className="text-sm font-medium leading-5 text-[var(--q3-neutral-light)]">Question no. 4</p>
              <div className="flex items-center gap-2">
                <span className="flex size-[26px] items-center justify-center rounded-full border-2 border-[var(--q3-accent-light)]">
                  <ClockIcon size={14} className="text-[var(--q3-accent-normal)]" />
                </span>
                <span className="text-base font-bold leading-6 text-[var(--q3-accent-normal)]">0:00</span>
              </div>
            </div>
            <div className="flex items-center gap-1.5 w-full rounded-md bg-[var(--q3-danger-light)] px-2.5 py-[5px]">
              <WarningIcon size={16} className="shrink-0 text-[var(--q3-danger-normal)]" />
              <p className="text-[10px] leading-4 text-[var(--q3-danger-normal)]">
                You&apos;re losing precious time on this question
              </p>
            </div>
          </div>
        </div>

        {/* Show per Question Timer setting */}
        <div className="w-full rounded-xl border border-[var(--q3-stroke-light)] bg-[var(--q3-surface-default)] p-4">
          <div className="flex items-center gap-4 w-full">
            <div className="flex flex-1 flex-col gap-1 justify-center min-w-0">
              <p className="text-base font-medium leading-6 text-[var(--q3-neutral-default)]">
                Show per Question Timer
              </p>
              <p className="text-sm leading-5 text-[var(--q3-neutral-light)]">
                Profile &gt; Settings &gt; Test &amp; analysis settings
              </p>
            </div>
            <ToggleSwitch
              id="showQuestionLevelTimer"
              checked={timerEnabled}
              disabled={syncing}
              onChange={handleToggle}
            />
          </div>
        </div>
      </div>

      <button
        className="mt-1 w-full rounded-full bg-[var(--q3-success-normal)] hover:bg-[var(--q3-success-dark)] text-[var(--q3-neutral-default_inverted)] font-semibold py-2 cursor-pointer transition-colors duration-200"
        onClick={onStart}
      >
        Start Test
      </button>
    </div>
  );
}

export default function QuestionLevelTimerModal({ open, onClose, onStart }) {
  const isDesktop = useMediaQuery('(min-width: 768px)');
  const modalRef = useRef(null);

  const clickHandler = (e) => {
    if (modalRef.current && !modalRef.current.contains(e.target)) {
      onClose();
    }
  };

  if (!open) return null;

  const wrapperClass = isDesktop
    ? 'fixed inset-0 z-50 flex items-center justify-center backdrop-blur-sm bg-black/40'
    : 'fixed inset-0 z-50 flex items-end justify-center backdrop-blur-sm bg-black/40';

  const cardClass = isDesktop
    ? 'relative bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-2xl p-4 max-w-md w-full mx-4'
    : 'relative bg-[var(--q3-surface-default)] border border-[var(--q3-stroke-normal)] rounded-t-3xl p-4 w-full';

  return (
    <div className={wrapperClass} onClick={clickHandler}>
      <div ref={modalRef} className={cardClass}>
        <ModalCloseButton onClick={onClose} className="-top-16" />
        <div className={isDesktop ? '' : 'max-h-[80vh] overflow-y-auto'}>
          <ModalBody onStart={onStart} />
        </div>
      </div>
    </div>
  );
}
